import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { colors } from '@guryou/colors';
import { ServiceTypes } from '@guryou/core';
import { stateIsLoading } from '@guryou/redux';
import { getReservationInitialDate } from '@guryou/utils';
import { Icon, SkeletonLoader } from '../../components/shared';
import AccordionDetailsCourse from './AccordionDetailsCourse';
import ServiceBookingModal from './ServiceBookingModal';
import { WidgetContext } from './WidgetContext';

const WidgetServices = ({ courseView, eventView }) => {
  const { t } = useTranslation();
  const dateTimeFrom = null;

  const dispatch = useDispatch();
  const { personnelTimeSlots, personnelDateSlots } = useSelector(state => state.widget);
  const {
    activeServiceId,
    setActiveServiceId,
    services,
    courses,
    sessionActiveStep,
    setSessionActiveStep,
    paymentOptions,
    primaryColor,
  } = useContext(WidgetContext);

  // Use custom primary color if available, otherwise fallback to default mandy color
  const primary = primaryColor || 'var(--feel-primary)';
  // Generate a secondary color (slightly lighter/darker) for gradients
  const secondary = primaryColor || colors.cabaret;

  const [selectedService, setSelectedService] = useState(0);
  const [hoveredService, setHoveredService] = useState(null);
  const [showMobileContent, setShowMobileContent] = useState(false);
  const [localData, setLocalData] = useState({
    resourceId: null,
    date: getReservationInitialDate(dateTimeFrom, personnelDateSlots),
    time: null,
  });

  const serviceOnline = eventView || courseView ? paymentOptions.data.courseOnline : paymentOptions.data.serviceOnline;
  const serviceOnSite = eventView || courseView ? paymentOptions.data.courseOnsite : paymentOptions.data.serviceOnsite;
  const serviceMembership = eventView || courseView ? paymentOptions.data.courseMembership : paymentOptions.data.serviceMembership;

  const listItems = courseView
    ? courses.data.filter(c => c.serviceType === ServiceTypes.course)
    : eventView
    ? courses.data.filter(c => c.serviceType === ServiceTypes.event)
    : services.data;

  const openOrCloseModal = ({ isExpanded, subservice }) => {
    if (isExpanded) {
      setActiveServiceId(subservice.id);
    } else {
      setActiveServiceId(null);
      setSessionActiveStep(0); // Reset to first step when closing modal
    }
  };

  // Don't show skeleton when modal is open - it would hide the modal
  if (stateIsLoading([personnelTimeSlots, personnelDateSlots]) && !activeServiceId) {
    return <SkeletonLoader type="provider" />;
  }

  return (
    <div className="w-full">
      {/* Modal for Services (not courses/events) - ServiceBookingModal handles this now */}

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Service Categories Sidebar */}
        <div className={`lg:w-80 flex-shrink-0 ${showMobileContent ? 'hidden lg:block' : 'block'}`}>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Icon name="book" size="sm" className="mr-2" style={{ color: primary }} />
              {courseView ? t('COURSES.titlePlural') : eventView ? t('EVENTS.titlePlural') : t('SERVICES.titlePlural')}
            </h3>
            <div className="space-y-2">
              {listItems?.map((service, idx) => (
                <button
                  key={service.id}
                  onClick={() => {
                    setSelectedService(idx);
                    setShowMobileContent(true);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center justify-between ${
                    selectedService === idx || hoveredService === idx ? `text-white shadow-lg` : `text-gray-600 hover:text-white hover:shadow-lg`
                  }`}
                  style={{
                    backgroundColor: selectedService === idx || hoveredService === idx ? primary : 'transparent',
                  }}
                  onMouseEnter={() => setHoveredService(idx)}
                  onMouseLeave={() => setHoveredService(null)}
                >
                  <span className="font-medium truncate">{service.name}</span>
                  <span
                    className={`text-sm px-2 py-1 rounded-full transition-all duration-200 ${
                      selectedService === idx || hoveredService === idx ? 'bg-white bg-opacity-20 text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {service?.offers?.length || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Services Content */}
        <div className={`flex-1 ${!showMobileContent ? 'hidden lg:block' : 'block'}`}>
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 lg:p-6 mb-4 lg:mb-6">
            <div className="flex items-center">
              {/* Mobile Back Button */}
              <button
                onClick={() => setShowMobileContent(false)}
                className="lg:hidden mr-3 p-1.5 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <Icon name="arrow-left" size="sm" className="text-gray-600" />
              </button>

              <div className="p-2 lg:p-3 rounded-full mr-3 lg:mr-4" style={{ backgroundColor: primary + '1A' }}>
                <Icon name={courseView ? 'read' : eventView ? 'calendar' : 'book'} size="md" className="lg:size-lg" style={{ color: primary }} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg lg:text-2xl font-bold text-gray-900 mb-0.5 lg:mb-1 truncate">{listItems[selectedService]?.name}</h2>
                <p className="text-sm lg:text-base text-gray-600 hidden sm:block">{t('WIDGET.servicesDescription')}</p>
              </div>
            </div>
          </div>

          {/* Services Grid - 2 Cards per Column */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {listItems[selectedService]?.offers?.map((subservice, index) => (
              <div key={subservice.id} className="group">
                {/* Modern Card with Project Colors */}
                <div
                  className="bg-gradient-to-br from-springWood to-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 relative overflow-hidden border border-mercury shadow-sm"
                  style={{
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  }}
                  onMouseEnter={e => {
                    const color = primary.replace('#', '');
                    e.currentTarget.style.boxShadow = `0 20px 25px -5px rgba(${parseInt(color.substring(0, 2), 16)}, ${parseInt(
                      color.substring(2, 4),
                      16
                    )}, ${parseInt(color.substring(4, 6), 16)}, 0.2)`;
                    e.currentTarget.style.borderColor = primary + '4D';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.borderColor = colors.mercury;
                  }}
                >
                  {/* Decorative Background Elements */}
                  <div className="absolute top-0 right-0 w-24 h-24 rounded-bl-full" style={{ backgroundColor: primary, opacity: 0.1 }}></div>
                  <div className="absolute bottom-0 left-0 w-20 h-20 rounded-tr-full" style={{ backgroundColor: primary, opacity: 0.1 }}></div>

                  {/* Card Content */}
                  <div className="relative z-10">
                    {/* Product Name */}
                    <div className="mb-4">
                      <h3 className="text-xl font-bold mb-2 leading-tight" style={{ color: colors.mineShaft }}>
                        {subservice?.name}
                      </h3>
                    </div>

                    {/* Service Details */}
                    <div className="space-y-3 mb-6">
                      {/* Duration for Services */}
                      {!courseView && !eventView && (
                        <div className="flex items-center" style={{ color: colors.boulder }}>
                          <Icon name="clock" size="sm" className="mr-3" style={{ color: primary }} />
                          <span className="text-sm">
                            {subservice?.duration} {t('GENERAL.minute')}
                          </span>
                        </div>
                      )}

                      {/* Sessions for Events */}
                      {eventView && (
                        <div className="flex items-center" style={{ color: colors.boulder }}>
                          <Icon name="calendar" size="sm" className="mr-3" style={{ color: primary }} />
                          <span className="text-sm">
                            {subservice?.sessions?.length} {t('GENERAL.session')}
                          </span>
                        </div>
                      )}

                      {/* Model for Courses */}
                      {courseView && (
                        <div className="flex items-center" style={{ color: colors.boulder }}>
                          <Icon name="info" size="sm" className="mr-3" style={{ color: primary }} />
                          <span className="text-sm">{subservice?.model}</span>
                        </div>
                      )}
                    </div>

                    {/* Bottom Section - Price and Button */}
                    <div className="flex items-center justify-between">
                      {/* Price */}
                      <div className="text-3xl font-bold" style={{ color: colors.mineShaft }}>
                        {subservice?.price} €
                      </div>

                      {/* Action Button */}
                      {(serviceOnline || serviceOnSite || serviceMembership) && (
                        <button
                          onClick={e => {
                            openOrCloseModal({
                              e,
                              isExpanded: true,
                              subservice,
                            });
                          }}
                          style={{
                            background: `linear-gradient(to right, ${primary}, ${secondary})`,
                          }}
                          className="text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 flex items-center shadow-lg hover:shadow-xl"
                          onMouseEnter={e => {
                            e.currentTarget.style.background = `linear-gradient(to right, ${secondary}, ${primary})`;
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = `linear-gradient(to right, ${primary}, ${secondary})`;
                          }}
                        >
                          <Icon name="shopping-cart" size="sm" className="mr-2" />
                          {t('GENERAL.payNow')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Service Booking Modal */}
                {!courseView && !eventView && activeServiceId === subservice.id && (
                  <ServiceBookingModal
                    subservice={subservice}
                    dispatch={dispatch}
                    localData={localData}
                    setLocalData={setLocalData}
                    personnelTimeSlots={personnelTimeSlots}
                    personnelDateSlots={personnelDateSlots}
                    closeModal={() => openOrCloseModal({ isExpanded: false, subservice })}
                  />
                )}

                {(courseView || eventView) && activeServiceId === subservice.id && (
                  <div className="mt-4">
                    <AccordionDetailsCourse
                      subservice={subservice}
                      sessionActiveStep={sessionActiveStep}
                      closeModal={() => openOrCloseModal({ isExpanded: false, subservice })}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetServices;
